// @ts-strict-ignore
import React, { useState, useCallback, useLayoutEffect, useMemo, useEffect, useRef } from 'react'
import { ScrollView, BackHandler, LayoutChangeEvent } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import styled, { useTheme } from 'styled-components/native'
import Reanimated, {
  SlideInRight,
  SlideInLeft,
  SlideOutLeft,
  SlideOutRight,
  runOnJS,
} from 'react-native-reanimated'

// Components
import ProgressBar from 'APP/Components/ProgressBar'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import IntakeQuestion from './IntakeQuestion'
import IntakeReplyPanel from './IntakeReplyPanel'
import Alert from 'APP/Converse/Alert'
import {
  RichContentTypeEnum,
  RichContentUnion,
  IntakeMessageTypeEnum,
} from 'APP/Services/Mattermost/types'
import IntakeCancelChat from './IntakeCancelChat'
import Button from 'APP/Converse/Button'

import Analytics from 'APP/Services/Analytics'
import I18n from 'APP/I18n'
import sprigClient from 'APP/Services/Sprig'
import { isWeb, isMobile, isAndroid } from 'APP/Helpers/checkPlatform'
import { ResponsiveView } from 'APP/Converse/Layout'
import { checkIsPrescriptionFlow, checkIsGynOrSexualHealthFlow } from 'APP/Helpers/Intake'
import { PortalPreview } from 'APP/Helpers/Portal'
import { ARCHIVE, UNDO } from './constants'
import { IntakeProvider, useIntakeContext } from './Context/IntakeProvider'
import IntakeHelpCentreButton from './IntakeHelpCentreButton'
import type { Episode } from 'APP/Store/History/types'

export { default as IntakeTransition } from './IntakeTransition'
export { REPLY_FORMS as INTAKE_REPLY_FORMS } from './IntakeReplyForm'

// TODO: Due to import of this property from /App/Containers/ChatScreen/index.js and
// the lack of time to also convert this file to ts, im reexporting the original
// variable here.
export const INTAKE_MESSAGE_TYPES = IntakeMessageTypeEnum

export const shouldUseMobileMultiSubpromptLayout = (isPromptMultiSubprompt: boolean) =>
  isMobile() && isPromptMultiSubprompt

// Same style as our ResponsiveView's innerContainer
const MultiSubpromptAnimatedContainer = styled(Reanimated.View)`
  width: 100%;
  max-width: 800px;
  align-self: center;
`

export const HeaderContainer = styled(ResponsiveView)<{ $compact?: boolean }>`
  display: flex;
  background-color: ${({ theme }) => theme.colors.background};
  padding-horizontal: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-top: ${({ theme }) => theme.metrics.statusBarHeight + theme.metrics.baseMargin}px;
  padding-bottom: ${({ theme }) => theme.metrics.baseMargin / 2}px;
  z-index: 2;
  width: 100%;
  flex-grow: ${({ $compact }) => ($compact ? 0 : 1)};
  flex-shrink: 0;
  align-items: flex-end;
`

export const IconContainer = styled.View`
  padding-top: ${({ theme }) => theme.metrics.baseMargin / 1.5}px;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`

export const ButtonWrapper = styled.View`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.metrics.baseMargin / 2}px;
`

export const SecondaryButton = styled(Button)`
  border: ${({ theme }) => theme.colors.buttonSecondaryOutline};
  text-transform: ${({ theme }) => theme.fonts.textTransform.button};
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
`

export const ExitIcon = styled(SecondaryButton)`
  padding: ${({ theme }) => theme.metrics.baseMargin - 5}px;
`

export const ActionBar = styled(ResponsiveView)`
  padding-top: ${({ theme }) => theme.metrics.baseMargin * 1.5}px;
`

export const WrapperView = styled.View`
  background-color: ${({ theme }) => theme.colors.background};
  flex: 1;
  z-index: 1;
`

export const StyledCard = styled(ScrollView)`
  flex-shrink: 1;
  z-index: 2;
  flex-basis: ${({ theme }) => theme.metrics.screenHeight}px;
  scrollbar-width: ${isWeb() ? 'none' : 'auto'};
`

const StaticCard = styled.View`
  flex: 1;
  z-index: 2;
  width: 100%;
  max-width: 800px;
  align-self: center;
`

enum CTA_TYPE {
  SAVE = 'save',
  EXIT = 'exit',
  ARCHIVE = 'archive',
}

type QuestionPost = {
  props?: Record<string, string>
  message: string
  channel_id: string
}

// TODO: use named arguments instead of positional
const trackSprigSurvey = (
  cta_type: CTA_TYPE,
  questionPost: QuestionPost,
  /** @deprecated Use `episodeFlowType` instead when possible. */
  episodeCommandPost?: string,
  isPrescriptionFlow?: boolean,
  isGynOrSexualHealthFlow?: boolean,
  episodeFlowType?: Episode['flowType']
) => {
  const eventProperties = {
    intake_utterance: questionPost?.props?.utterance,
    intake_message: questionPost?.message,
    episode_id: questionPost?.channel_id,
  }

  if (isPrescriptionFlow) {
    sprigClient.track('MX_NC_PRESCRIPTION_DROPOFF', eventProperties)
  } else if (isGynOrSexualHealthFlow) {
    sprigClient.track('MX_GYN_DROPOFF', eventProperties)
  }

  sprigClient.track('MX_DROPOFF_INTAKE', {
    ...eventProperties,
    cta_event: cta_type,
    intake_command: episodeCommandPost?.replace('/r ', ''),
    appointment_type: questionPost?.props?.appointment_type,
    checkout_type: questionPost?.props?.checkout_type,
    flow_type: episodeFlowType,
  })
}

const hasExitAlert = (exitAlert?: Record<string, string>) =>
  exitAlert && Object.keys(exitAlert).length

interface OnIntakeExitParams {
  exitAlert?: Record<string, string>
  navigation: any // @todo: ANY - be more specific here
  questionPost: QuestionPost
  isIntakeCancelable: boolean
  isPrescriptionFlow: boolean
  isGynOrSexualHealthFlow: boolean
  /** @deprecated Use `episodeFlowType` instead when possible. */
  episodeCommandPost?: string
  episodeFlowType: Episode['flowType']
}

const onIntakeExit = ({
  exitAlert,
  navigation,
  questionPost,
  isIntakeCancelable,
  isPrescriptionFlow,
  isGynOrSexualHealthFlow,
  episodeCommandPost,
  episodeFlowType,
}: OnIntakeExitParams) => {
  if (!hasExitAlert(exitAlert) && isIntakeCancelable) {
    return
  }

  const exitText = exitAlert?.exit || I18n.t('Intake.alert.exit')
  const continueText = exitAlert?.continue || I18n.t('Intake.alert.continue')
  const exit = {
    text: exitText,
    onPress: () => {
      Analytics.trackEvent('button_click', {
        button_value: 'Intake exit',
        button_text: exitText,
      })
      navigation.goBack()
      trackSprigSurvey(
        CTA_TYPE.EXIT,
        questionPost,
        episodeCommandPost,
        isPrescriptionFlow,
        isGynOrSexualHealthFlow,
        episodeFlowType
      )
    },
  }
  const continueTriage = {
    text: continueText,
    onPress: () => {
      Analytics.trackEvent('button_click', {
        button_value: 'Intake continue',
        button_text: continueText,
      })
    },
  }
  const options = isAndroid() ? [exit, continueTriage] : [continueTriage, exit]

  Alert.alert(
    exitAlert?.title || I18n.t('Intake.alert.title'),
    exitAlert?.description || I18n.t('Intake.alert.description'),
    options
  )
  // Ensures Alert stays in place until user decides their action
  return true
}

const getLayoutConfig = ({ props: { type } = {} } = {}) => ({
  // shrinkCard === true:
  // * screen is not scrollable (does not support overflow, available screen space is divided between children)
  shrinkCard: ![
    IntakeMessageTypeEnum.prompt_choice_multi,
    IntakeMessageTypeEnum.prompt_multi_subprompt,
    !isWeb() && IntakeMessageTypeEnum.prompt_provider_list,
  ].includes(type),
  // shrinkReplyPanel === true:
  // * IntakeReplyPanel takes up the least amount of space
  shrinkReplyPanel: ![
    IntakeMessageTypeEnum.prompt_text,
    IntakeMessageTypeEnum.prompt_choice_multi,
    !isWeb() && IntakeMessageTypeEnum.prompt_choice_search,
    !isWeb() && IntakeMessageTypeEnum.prompt_choice_image_map,
    IntakeMessageTypeEnum.prompt_booking,
    !isWeb() && IntakeMessageTypeEnum.prompt_address,
    IntakeMessageTypeEnum.prompt_checkout,
  ].includes(type),
  // replyPanelNoGrow === true:
  // * IntakeReplyPanel would not be growing to take up space
  // * answers are always rendered in absolute overlay
  // tips:
  // * use when ReplyForm needs to take all available space (+ overlayReplyForm === true)
  replyPanelNoGrow: [
    IntakeMessageTypeEnum.prompt_choice_image_map,
    isWeb() && IntakeMessageTypeEnum.prompt_choice_search,
    IntakeMessageTypeEnum.prompt_address,
    isMobile() && IntakeMessageTypeEnum.prompt_choice_search,
    IntakeMessageTypeEnum.prompt_booking,
    IntakeMessageTypeEnum.prompt_checkout,
  ].includes(type),
  // overlayReplyForm === true:
  // * replyForm is rendered INLINE & OUTSIDE of replyPanel as the very last & direct child of <Card />
  overlayReplyForm: [
    IntakeMessageTypeEnum.prompt_choice_search,
    IntakeMessageTypeEnum.prompt_choice_image_map,
    IntakeMessageTypeEnum.prompt_booking,
    IntakeMessageTypeEnum.prompt_address,
    IntakeMessageTypeEnum.prompt_checkout,
  ].includes(type),
  // expandReplyForm === true:
  // * replyForm is expanded to take all available space
  expandReplyForm: [IntakeMessageTypeEnum.prompt_file].includes(type),
})

interface HeaderProps {
  processing: boolean
  fullscreenState: {
    progress: {
      current: number
      total: number
    }
  }
  onExit: () => void
  onGoBack: () => void
  canGoBack: boolean
  questionPost: unknown
  compact?: boolean
}

const Header: React.FC<HeaderProps> = ({
  processing,
  fullscreenState: { progress },
  onExit,
  onGoBack,
  canGoBack,
  questionPost,
  compact,
}) => {
  const theme = useTheme()
  const { setHeaderHeight } = useIntakeContext()

  const onLayout = (event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout
    setHeaderHeight(height)
  }

  return (
    <HeaderContainer $compact={compact} onLayout={onLayout}>
      <ProgressBar processing={processing} show={!!progress} {...progress} />
      <IconContainer canGoBack={canGoBack}>
        <SecondaryButton
          title={I18n.t('AccessibilityIcons.back')}
          variant="secondary"
          onPress={onGoBack}
          analyticsName="Intake go back"
          testID="INTAKE_BACK_BTN"
          accessibilityLabel={I18n.t('AccessibilityIcons.back')}
          iconPosition="left"
          icon="arrow-back-ios-new"
          iconVariant="materialIcons"
          disabled={!canGoBack}
          iconColor={canGoBack ? 'text' : 'buttonSecondaryDisabled'}
          widthVariant="content"
          iconSize={theme.metrics.icon.tiny}
        />
        <ButtonWrapper>
          <IntakeHelpCentreButton questionPost={questionPost} />
          <ExitIcon
            variant="secondary"
            onPress={onExit}
            analyticsName="Intake close"
            testID="INTAKE_CLOSE_BTN"
            accessibilityLabel={I18n.t('AccessibilityIcons.exit')}
            iconPosition="left"
            icon="close"
            iconVariant="materialIcons"
            iconColor="text"
            widthVariant="content"
            iconSize={theme.metrics.icon.small}
          />
        </ButtonWrapper>
      </IconContainer>
    </HeaderContainer>
  )
}

interface CardProps {
  shrink: boolean
  children: React.ReactNode
  scrollEnabled?: boolean
  questionId?: string
}

const Card: React.FC<CardProps> = ({ shrink, children, scrollEnabled, questionId }) => {
  const { scrollViewRef } = useIntakeContext()

  useEffect(() => {
    scrollViewRef.current?.scrollTo({ y: 0, animated: false })
  }, [questionId, scrollViewRef])

  if (scrollEnabled === false) {
    return <StaticCard>{children}</StaticCard>
  }

  return (
    <StyledCard
      ref={scrollViewRef}
      contentContainerStyle={[
        {
          flexGrow: 1,
          flexShrink: 0,
          justifyContent: 'space-between',
          width: '100%',
          maxWidth: 800,
          alignSelf: 'center',
        },
        shrink && { flexShrink: 1 },
      ]}
      keyboardShouldPersistTaps={true}
      alwaysBounceVertical={false}
      scrollEnabled={scrollEnabled}
      stickyHeaderIndices={[0]}
    >
      {children}
    </StyledCard>
  )
}

const ContentContainer = styled(Reanimated.ScrollView)`
  flex: 1;
`

const StaticContentContainer = styled(Reanimated.View)`
  flex: 1;
`

interface IntakeProps {
  posts: Record<string, any> // @todo: ANY - be more specific here
  order: string[]
  userId: string
  fullscreenState?: any // @todo: ANY - be more specific here
  onIntakeAnswerSend: (answer: any) => void // @todo: ANY - be more specific here
  retryPost: (postId: string) => void
  resolveAttachmentUrl: (url: string) => string
  resolveGetPostFileMetadata: (fileId: string) => any // @todo: ANY - be more specific here
  error?: boolean
  exitAlert?: Record<string, string>
  channelId: string
  exitAction?: () => void
}

const Intake: React.FC<IntakeProps> = ({
  posts,
  order,
  userId,
  fullscreenState = {},
  onIntakeAnswerSend,
  retryPost,
  resolveAttachmentUrl,
  resolveGetPostFileMetadata,
  error,
  exitAlert,
  channelId,
  exitAction,
}) => {
  const navigation = useNavigation()
  const dispatch = useDispatch()
  const contentContainerRef = useRef<Reanimated.ScrollView>(null)

  const [questionShown, setQuestionShown] = useState(false)
  const onQuestionShow = useCallback(() => setQuestionShown(true), [])

  const [isGoingBack, setIsGoingBack] = useState(false)
  const [multiSubpromptPortalReadyId, setMultiSubpromptPortalReadyId] = useState<string>()

  const [displayCancelChatModal, triggerCancelChatModal] = useState(false)

  const { archiveEpisodeRunning, undonePostIds } = useSelector((state) => state.history)
  const episode = useSelector((state) => state.history.episodes?.[channelId])

  const [questionPost, answerPosts] = useMemo(() => {
    const lastQuestionIndex = order.findIndex((id) => posts[id].user_id !== userId)
    const lastQuestionPost = posts[order[lastQuestionIndex]]
    let lastAnswerPosts = []

    if (lastQuestionIndex > 0) {
      lastAnswerPosts = order
        .slice(0, lastQuestionIndex)
        .map((id) => posts[id])
        // Filter failed posts not related to current question as they end up on the top of the episode
        .filter(({ create_at }) => create_at > lastQuestionPost.create_at)
    }
    return [lastQuestionPost, lastAnswerPosts]
  }, [posts, order, userId])
  const onMultiSubpromptPortalReady = useCallback(
    (postId: string) => setMultiSubpromptPortalReadyId(postId),
    []
  )

  const {
    isPrescriptionFlow,
    isGynOrSexualHealthFlow,
    isIntakeCancelable,
    backButtonAction,
    canGoBack,
    /** @deprecated Use `episodeFlowType` instead when possible. */
    episodeCommandPost,
    episodeFlowType,
  } = useMemo(() => {
    const isEpisodeArchivable = !!episode?.isArchivable
    const episodeCommandPost = episode?.commandPost
    const episodeFlowType = episode?.flowType
    const isBackButtonEnabled = questionPost?.props?.member_app_fullscreen?.back?.enabled ?? false

    return {
      isPrescriptionFlow: checkIsPrescriptionFlow(posts, questionPost),
      isGynOrSexualHealthFlow: checkIsGynOrSexualHealthFlow(posts),
      // In case of new episodes, isArchivable will be undefined so we default to true to make the chat cancellable
      isIntakeCancelable: isEpisodeArchivable,
      // Values: 'ARCHIVE', 'UNDO'
      backButtonAction: questionPost?.props?.member_app_fullscreen?.back?.app_action ?? 'UNDO',
      canGoBack: isEpisodeArchivable && isBackButtonEnabled,
      episodeCommandPost,
      episodeFlowType,
    }
  }, [posts, questionPost, episode])

  const onExit = useCallback(() => {
    if (exitAction) {
      exitAction()
    } else {
      triggerCancelChatModal(isIntakeCancelable)
      onIntakeExit({
        exitAlert,
        navigation,
        questionPost,
        isIntakeCancelable,
        isPrescriptionFlow,
        isGynOrSexualHealthFlow,
        episodeCommandPost,
        episodeFlowType,
      })
    }
    return true // Prevent default back behavior
  }, [
    exitAlert,
    navigation,
    questionPost,
    exitAction,
    isIntakeCancelable,
    isPrescriptionFlow,
    isGynOrSexualHealthFlow,
    episodeCommandPost,
    episodeFlowType,
  ])

  const onCloseModal = () => {
    triggerCancelChatModal(false)
  }

  /* istanbul ignore next */
  const onSaveChat = () => {
    navigation.goBack()
    trackSprigSurvey(
      CTA_TYPE.SAVE,
      questionPost,
      episodeCommandPost,
      undefined,
      undefined,
      episodeFlowType
    )
  }

  const onArchiveEpisode = () => {
    let sprigSurveyData = {}
    if (isGynOrSexualHealthFlow) {
      sprigSurveyData = {
        trackName: 'MX_GYN_DROPOFF',
        eventProperties: {
          intake_utterance: questionPost?.props?.utterance,
          intake_message: questionPost?.message,
        },
      }
    }
    dispatch(PatientHistoryActions.archiveEpisodeRequest(channelId, sprigSurveyData))
    trackSprigSurvey(
      CTA_TYPE.ARCHIVE,
      questionPost,
      episodeCommandPost,
      undefined,
      undefined,
      episodeFlowType
    )
  }

  useEffect(() => {
    if (displayCancelChatModal) {
      Analytics.trackScreen('Intake ask cancel chat modal')
    }
  }, [displayCancelChatModal])

  useEffect(() => {
    if (isWeb()) {
      return
    }

    const subscription = BackHandler.addEventListener('hardwareBackPress', onExit)
    return () => subscription.remove()
  }, [onExit])

  const layoutConfig = useMemo(() => getLayoutConfig(questionPost), [questionPost])

  useLayoutEffect(() => {
    setQuestionShown(false)
  }, [])

  useEffect(() => {
    if (isWeb() && contentContainerRef.current && questionPost?.id) {
      setTimeout(() => {
        ;(contentContainerRef.current as unknown as HTMLDivElement)?.focus()
      }, animationDuration)
    }
  }, [questionPost?.id])

  const { shrinkCard, shrinkReplyPanel, replyPanelNoGrow, overlayReplyForm, expandReplyForm } =
    layoutConfig
  const processing =
    (answerPosts &&
      answerPosts.length > 0 &&
      !answerPosts.some((ans) => ans.props && ans.props.failed)) ||
    !questionShown

  // Simulate loading steps
  const [currentStepIndex, setCurrentStepIndex] = useState(1)

  const loadingConfig = useMemo(() => {
    const findSteps = (richContentItems: RichContentUnion[]): string[] | undefined => {
      for (const item of richContentItems) {
        if (item.type === RichContentTypeEnum.Steps) return item.steps
        if (item.type === RichContentTypeEnum.GroupedSection && item.items) {
          const found = findSteps(item.items)
          if (found) return found
        }
      }
      return undefined
    }

    const description = questionPost?.props?.structured_description
    return description ? findSteps(description) : false
  }, [questionPost?.props?.structured_description])

  useEffect(() => {
    setCurrentStepIndex(1)
  }, [questionPost?.id])

  useEffect(() => {
    if (loadingConfig && currentStepIndex < loadingConfig.length + 1) {
      const timer = setTimeout(() => {
        setCurrentStepIndex(currentStepIndex + 1)
      }, Math.random() * 1000 + 500)
      return () => clearTimeout(timer)
    }
  }, [loadingConfig, currentStepIndex])

  const displayLoadingAnswer = loadingConfig && currentStepIndex > loadingConfig.length

  const isPromptMultiSubprompt = questionPost?.props?.type == 'prompt_multi_subprompt'
  const useMobileMultiSubpromptLayout = shouldUseMobileMultiSubpromptLayout(isPromptMultiSubprompt)
  const isPromptFile = questionPost?.props?.type == 'prompt_file'
  const isPreparingMultiSubprompt =
    isPromptMultiSubprompt && multiSubpromptPortalReadyId !== questionPost?.id

  const onGoBack = () => {
    const goBackRunning = episode?.goBack?.running
    const postAlreadyUndone = undonePostIds?.indexOf(questionPost?.id) !== -1

    // Whether we're archiving or undoing a post, we prevent another back event
    // from being triggered, as they can interfere with one another.
    if (archiveEpisodeRunning || goBackRunning || postAlreadyUndone) {
      return
    }

    // Set direction for backwards animation tracking
    setIsGoingBack(true)

    if (backButtonAction === ARCHIVE) {
      onArchiveEpisode()
      return
    }

    if (backButtonAction === UNDO) {
      dispatch(PatientHistoryActions.goBackInEpisodeRequest(channelId, questionPost?.id))
    }
  }

  const animationDuration = 250
  const resetDirection = () => setIsGoingBack(false)

  // Reanimated requires a worklet to call sync functions like setState
  // https://docs.swmansion.com/react-native-reanimated/docs/guides/worklets/
  const animationCompleteCallback = () => {
    'worklet'
    runOnJS(resetDirection)()
  }

  const contentAnimationConfig = {
    entering: isGoingBack
      ? SlideInLeft.duration(animationDuration).withCallback(animationCompleteCallback)
      : SlideInRight.duration(animationDuration),
    exiting: isGoingBack
      ? SlideOutRight.duration(animationDuration).withCallback(animationCompleteCallback)
      : SlideOutLeft.duration(animationDuration),
  }
  const contentContainerKey = isPreparingMultiSubprompt
    ? `preparing-content-container--${questionPost?.id}`
    : questionPost && `content-container--${questionPost.id}`
  const synchronizedContentAnimationConfig = isPreparingMultiSubprompt ? {} : contentAnimationConfig

  return (
    <IntakeProvider>
      <WrapperView>
        <Card
          shrink={shrinkCard}
          scrollEnabled={useMobileMultiSubpromptLayout ? false : undefined}
          questionId={questionPost?.id}
        >
          <Header
            fullscreenState={fullscreenState}
            processing={processing}
            onExit={onExit}
            onGoBack={onGoBack}
            canGoBack={canGoBack}
            questionPost={questionPost}
            compact={useMobileMultiSubpromptLayout || undefined}
          />
          {!error && (
            <ContentContainer
              {...(useMobileMultiSubpromptLayout ? { as: StaticContentContainer } : {})}
              key={contentContainerKey}
              ref={contentContainerRef}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ flexGrow: 1 }}
              tabIndex={0}
              style={isPreparingMultiSubprompt ? { opacity: 0 } : undefined}
              {...synchronizedContentAnimationConfig}
            >
              <IntakeQuestion
                // Why are we overriding this, and then rendering the same thing in the reply panel?
                // Messes up lots of things relating to animation
                post={!isPromptMultiSubprompt ? questionPost : null}
                onQuestionShow={onQuestionShow}
                currentStepIndex={currentStepIndex}
                multiPromptUtteranceKey={
                  isPromptMultiSubprompt ? questionPost?.props?.utterance : null
                }
                scrollEnabled={false}
              />
              <IntakeReplyPanel
                shrink={shrinkReplyPanel}
                noGrow={replyPanelNoGrow}
                overlayReplyForm={overlayReplyForm}
                expandReplyForm={expandReplyForm}
                showReplyPanel={loadingConfig ? displayLoadingAnswer : questionShown}
                onAnswerSend={onIntakeAnswerSend}
                answers={answerPosts}
                post={questionPost}
                retryPost={retryPost}
                resolveAttachmentUrl={resolveAttachmentUrl}
                resolveGetPostFileMetadata={resolveGetPostFileMetadata}
              />
            </ContentContainer>
          )}
        </Card>
        {isPromptFile && <PortalPreview name="intakeFileUploadPortal" />}
        {isPromptMultiSubprompt && (
          <MultiSubpromptAnimatedContainer
            key={contentContainerKey}
            style={isPreparingMultiSubprompt ? { opacity: 0 } : undefined}
            {...synchronizedContentAnimationConfig}
          >
            <PortalPreview
              name="multiSubPromptSubmitPortal"
              onReady={() => onMultiSubpromptPortalReady(questionPost?.id)}
            />
          </MultiSubpromptAnimatedContainer>
        )}
      </WrapperView>

      {!hasExitAlert(exitAlert) && (
        <IntakeCancelChat
          isVisible={displayCancelChatModal}
          onCancelChat={onArchiveEpisode}
          onCloseModal={onCloseModal}
          onSaveChat={onSaveChat}
          isCancelling={archiveEpisodeRunning}
        />
      )}
    </IntakeProvider>
  )
}

export default Intake
